import { AppBar, Box, Divider, IconButton, MenuItem, Toolbar, Typography, Button } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import visibleStyles from './visibletracks.module.css';
import headerStyles from '../trackfavmenu.module.css';
import AppContext from '../../context/AppContext';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as BackIcon } from '../../assets/icons/ic_arrow_back.svg';
import CloudTrackItem from '../tracks/CloudTrackItem';
import EmptyVisible from '../errors/EmptyVisible';
import isEmpty from 'lodash-es/isEmpty';
import { DEFAULT_GROUP_NAME, getFileName, TRACK_VISIBLE_FLAG } from '../../manager/track/TracksManager';
import Empty from '../errors/Empty';
import { hideAllTracks, showAllVisibleTracks } from '../../manager/track/DeleteTrackManager';
import { useTranslation } from 'react-i18next';
import { SHARE_TYPE } from '../share/shareConstants';
import { useRecentDataSaver } from '../../util/hooks/menu/useRecentDataSaver';
import Loading from '../errors/Loading';
import {
    CONFIGURE_URL,
    MAIN_URL_WITH_SLASH,
    MENU_IDS,
    TRACKS_URL,
    HEADER_SIZE,
    liveHash,
} from '../../manager/GlobalManager';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWindowSize } from '../../util/hooks/useWindowSize';
import gStyles from '../gstylesmenu.module.css';

export const VISIBLE_SHARE_MARKER = SHARE_TYPE + '_visible_marker_';

export function getCountVisibleTracks(visibleTracks) {
    return visibleTracks?.new?.length || 0;
}

// shared files are always kept in the visible cache with the SHARE_TYPE prefix
function visibleCacheName(file, shared) {
    return shared ? VISIBLE_SHARE_MARKER + file.name : file.name;
}

export function isVisibleTrack(file) {
    const savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG));
    return !!savedVisible?.open?.includes(visibleCacheName(file, file.sharedWithMe));
}

export function updateVisibleCache({ visible, file, smartf = null }) {
    const savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG)) ?? { old: [], new: [] };
    if (!savedVisible.open) {
        savedVisible.open = [];
    }
    const fileName = visibleCacheName(file, smartf?.type === SHARE_TYPE);

    if (visible) {
        if (!savedVisible.open.includes(fileName)) {
            savedVisible.open.push(fileName);
        }
    } else {
        const ind = savedVisible.open.findIndex((n) => n === fileName);
        if (ind !== -1) {
            savedVisible.open.splice(ind, 1);
        }
    }
    localStorage.setItem(TRACK_VISIBLE_FLAG, JSON.stringify(savedVisible));
}

export function hideAllVisTracks() {
    const savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG));
    if (!savedVisible) {
        return;
    }
    savedVisible.open = [];
    localStorage.setItem(TRACK_VISIBLE_FLAG, JSON.stringify(savedVisible));
}

export function showAllVisTracks() {
    const savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG));
    if (!savedVisible) {
        return;
    }
    savedVisible.open = [...(savedVisible.old || []), ...(savedVisible.new || [])];
    localStorage.setItem(TRACK_VISIBLE_FLAG, JSON.stringify(savedVisible));
}

export function addCloseTracksToRecently(ctx) {
    if (!isEmpty(ctx.visibleTracks)) {
        let savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG));
        let newVisFiles = {
            old: ctx.visibleTracks.old,
            new: [],
        };

        let newVisFilesNames = {
            old: savedVisible.old,
            new: [],
            open: [],
        };

        ctx.visibleTracks.new?.forEach((t) => {
            const sharedFile = t.sharedWithMe;

            const fileName = visibleCacheName(t, sharedFile);
            if (savedVisible.open?.includes(fileName)) {
                newVisFiles.new.push(t);
                newVisFilesNames.new.push(fileName);
                newVisFilesNames.open.push(fileName);
            } else {
                newVisFiles.old.unshift(t);
                newVisFilesNames.old.unshift(fileName);
            }
        });

        localStorage.setItem(TRACK_VISIBLE_FLAG, JSON.stringify(newVisFilesNames));
        ctx.setVisibleTracks({ ...newVisFiles });
    }
}

function allVisibleTracksHidden() {
    const savedVisible = JSON.parse(localStorage.getItem(TRACK_VISIBLE_FLAG));
    if (savedVisible?.open) {
        return savedVisible.open.length === 0;
    }
    return true;
}

export default function VisibleTracks({ source, open }) {
    const ctx = useContext(AppContext);

    const navigate = useNavigate();
    const location = useLocation();

    const { t } = useTranslation();
    const [, height] = useWindowSize();

    const recentSaver = useRecentDataSaver();

    const [allTracksHidden, setAllTracksHidden] = useState(() => allVisibleTracksHidden());

    useEffect(() => {
        setAllTracksHidden(allVisibleTracksHidden());
    }, [ctx.visibleTracks]);

    const isLoading = ctx.gpxLoading;
    const showEmptyVisible = isEmpty(ctx.visibleTracks?.new) && hasTracks();

    const trackItems = useMemo(() => {
        const items = [];
        ctx.visibleTracks?.new?.map((file, index) => {
            const trackName = getFileName(file);
            const smartf = file.sharedWithMe ? { type: SHARE_TYPE } : null;
            const isLastItem = !isEmpty(ctx.visibleTracks?.new) ? index === ctx.visibleTracks?.new.length - 1 : false;
            if (file.filesize !== 0) {
                items.push(
                    <CloudTrackItem
                        id={`se-new-visible-track-${trackName}`}
                        key={'visible-cloudtrack-' + file.name}
                        file={file}
                        visible={true}
                        isLastItem={isLastItem}
                        smartf={smartf}
                    />
                );
            }
        });
        return items;
    }, [ctx.visibleTracks?.new]);

    const trackItemsOld = useMemo(() => {
        const items = [];
        ctx.visibleTracks?.old?.map((file, index) => {
            const trackName = getFileName(file);
            const smartf = file.sharedWithMe ? { type: SHARE_TYPE } : null;
            const isLastItem = !isEmpty(ctx.visibleTracks?.old) ? index === ctx.visibleTracks?.old.length - 1 : false;
            if (file.filesize !== 0) {
                items.push(
                    <CloudTrackItem
                        id={`se-old-visible-track-${trackName}`}
                        key={'visible-cloudtrack-' + file.name}
                        file={file}
                        visible={true}
                        isLastItem={isLastItem}
                        smartf={smartf}
                    />
                );
            }
        });
        return items;
    }, [ctx.visibleTracks?.old]);

    function hasVisibleTracks() {
        return !isEmpty(ctx.visibleTracks?.old) || !isEmpty(ctx.visibleTracks?.new);
    }

    function hasTracks() {
        if (!isEmpty(ctx.listFiles)) {
            return ctx.listFiles.uniqueFiles.some((f) => f.type === 'GPX');
        }
        if (!isEmpty(ctx.shareWithMeFiles?.tracks)) {
            return ctx.shareWithMeFiles.tracks?.length > 0;
        }
        return false;
    }

    const ToggleButton = () => {
        const id = allTracksHidden ? 'se-show-all-visible-tracks' : 'se-hide-all-visible-tracks';
        const label = allTracksHidden ? t('shared_string_show_all') : t('shared_string_hide_all');
        return (
            <Button
                id={id}
                className={visibleStyles.button}
                onClick={() => {
                    if (allTracksHidden) {
                        showAllVisibleTracks(ctx, recentSaver);
                    } else {
                        hideAllTracks(ctx);
                    }
                }}
            >
                {label}
            </Button>
        );
    };

    return (
        <>
            <Box sx={{ height: `${height - HEADER_SIZE}px` }} className={gStyles.scrollMainBlock}>
                <AppBar position="static" className={headerStyles.appbar}>
                    <Toolbar className={headerStyles.toolbar}>
                        <IconButton
                            id={
                                source
                                    ? `se-visible-tracks-button-back-${
                                          source === MENU_IDS.config
                                              ? 'config'
                                              : source === MENU_IDS.tracks
                                                ? 'tracks'
                                                : source
                                      }`
                                    : 'se-visible-tracks-button-close'
                            }
                            variant="contained"
                            type="button"
                            className={headerStyles.appBarIcon}
                            onClick={() => {
                                ctx.setOpenVisibleMenu((prev) => ({
                                    ...prev,
                                    open: false,
                                    ...(source === MENU_IDS.config && { showConfig: false }),
                                    ...(source === MENU_IDS.tracks && { showTracks: false }),
                                }));

                                if (source === MENU_IDS.config) {
                                    navigate(MAIN_URL_WITH_SLASH + CONFIGURE_URL + liveHash());
                                } else if (source === MENU_IDS.tracks) {
                                    navigate(MAIN_URL_WITH_SLASH + TRACKS_URL + liveHash());
                                } else {
                                    navigate(MAIN_URL_WITH_SLASH + liveHash());
                                }

                                if (open) open(false);
                            }}
                        >
                            {source ? <BackIcon /> : <CloseIcon />}
                        </IconButton>
                        <Typography id="se-visible-cloud-track-name" component="div" className={headerStyles.title}>
                            {t('shared_string_tracks')}
                        </Typography>
                        {!isLoading && <ToggleButton />}
                    </Toolbar>
                </AppBar>
                {isLoading ? (
                    <Loading />
                ) : (
                    <Box className={gStyles.scrollActiveBlock}>
                        {showEmptyVisible && <EmptyVisible id="se-empty-visible" />}
                        {hasVisibleTracks() && (
                            <>
                                <Box minWidth={ctx.infoBlockWidth} maxWidth={ctx.infoBlockWidth}>
                                    {trackItems}
                                </Box>
                                {trackItemsOld.length > 0 && (
                                    <>
                                        <Divider />
                                        <MenuItem className={visibleStyles.item}>
                                            <Typography className={visibleStyles.title} noWrap>
                                                {t('web:recently_visible')}
                                            </Typography>
                                        </MenuItem>
                                        <Box minWidth={ctx.infoBlockWidth} maxWidth={ctx.infoBlockWidth}>
                                            {trackItemsOld}
                                        </Box>
                                    </>
                                )}
                            </>
                        )}
                        {!hasVisibleTracks() && !hasTracks() && (
                            <Empty
                                title={"You don't have track files"}
                                text={'You can import, create track files using OsmAnd.'}
                                folder={DEFAULT_GROUP_NAME}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </>
    );
}
